/**
 * Sanitize model messages for Bedrock Converse API.
 * Single place that guarantees: no empty content, no blank text in blocks.
 *
 * The AI SDK's convertToLanguageModelMessage (run inside streamText) strips:
 * - assistant parts with type 'tool-approval-request'
 * - assistant parts with type 'text' and text === ''
 * So assistant messages that only have those parts end up with content: [] and Bedrock rejects.
 * We ensure every assistant message has at least one part that survives that filter.
 *
 * @ai-sdk/amazon-bedrock convertToBedrockChatMessages then skips assistant text parts where
 * !part.text.trim() (whitespace-only), so we use '.' not ' ' for placeholders.
 *
 * Use this:
 * - In the message handler after convertToModelMessages (before passing to streamBedrockResponse).
 * - Inside streamBedrockResponse right before streamText, so we never send invalid payloads.
 */

import type { BedrockMessage, BedrockMessageRole, BedrockContentBlock } from './message-types.js';
import type { ModelMessageInput } from './message-types.js';
import { MAX_CONTENT_BLOCK_CHARS, TRUNCATION_SUFFIX } from './constants.js';
import { logger } from '../logger.js';

/** Non-whitespace so @ai-sdk/amazon-bedrock won't skip it (it skips text where !part.text.trim()) */
const PLACEHOLDER_BLOCK: BedrockContentBlock = { type: 'text', text: '.' };

function isBlockWithText(block: unknown): block is Record<string, unknown> & { text?: unknown } {
  return typeof block === 'object' && block !== null && 'text' in block;
}

function truncateForBedrock(value: string): string {
  if (value.length <= MAX_CONTENT_BLOCK_CHARS) return value;
  return value.slice(0, MAX_CONTENT_BLOCK_CHARS) + TRUNCATION_SUFFIX;
}

function ensureBlockTextSafe(block: Record<string, unknown>): BedrockContentBlock {
  // Tool-result blocks: use canonical 'output' field with raw value (object or string). No double-serialization.
  if (block.type === 'tool-result' || 'result' in block || 'output' in block) {
    const raw = block.output ?? block.result;
    if (raw !== undefined && raw !== null) {
      // Pass through raw value; only truncate when it's already a string and too long
      const safe =
        typeof raw === 'string' && raw.length > MAX_CONTENT_BLOCK_CHARS
          ? truncateForBedrock(raw)
          : raw;
      if (typeof raw === 'string' && raw.length > MAX_CONTENT_BLOCK_CHARS) {
        logger.warn('truncating oversized tool result', {
          op: 'sanitizeToBedrockMessages',
          type: block.type,
          originalLength: raw.length,
          maxChars: MAX_CONTENT_BLOCK_CHARS,
        });
      }
      const { result: _result, output: _output, ...cleanBlock } = block;
      return { ...cleanBlock, output: safe } as BedrockContentBlock;
    }
  }

  if (!isBlockWithText(block)) return block as BedrockContentBlock;
  let text = block.text;
  if (text == null || String(text).trim() === '') {
    // Log when we're replacing empty text with placeholder
    if (block.type === 'text') {
      logger.warn('replacing empty text block with placeholder', { op: 'sanitizeToBedrockMessages' });
    }
    return { ...block, text: '.' } as BedrockContentBlock;
  }
  const str = String(text);
  if (str.length > MAX_CONTENT_BLOCK_CHARS) {
    logger.warn('truncating oversized content block', {
      op: 'sanitizeToBedrockMessages',
      type: block.type,
      originalLength: str.length,
      maxChars: MAX_CONTENT_BLOCK_CHARS,
    });
    text = truncateForBedrock(str);
  }
  return { ...block, text } as BedrockContentBlock;
}

/** Parts that the AI SDK strips from assistant messages (so they become empty after conversion) */
function survivesSdkAssistantFilter(block: Record<string, unknown>): boolean {
  if (block.type === 'tool-approval-request') return false;
  if (block.type === 'text' && (block.text == null || String(block.text).trim() === '')) return false;
  return true;
}

/**
 * Block is a tool_use (assistant requested a tool). Bedrock requires tool results to follow.
 * Detects both Bedrock format (tool_use, toolUseId) and AI SDK ModelMessage format (tool-call, toolCallId).
 */
function isToolUseBlock(block: Record<string, unknown>): boolean {
  const t = block.type;
  return (
    t === 'tool_use' ||
    t === 'toolUse' ||
    t === 'tool-use' ||
    t === 'tool-call' ||
    typeof block.toolUseId === 'string' ||
    typeof block.toolCallId === 'string' ||
    (typeof block.id === 'string' && block.name != null)
  );
}

/**
 * Strip tool_use blocks from assistant messages when there's no valid tool result following.
 * Bedrock rejects: "tool_use ids were found without tool_result blocks immediately after".
 * Context-window slicing can drop the tool message and leave orphan tool_use; this fixes that.
 *
 * Note: Bedrock doesn't support a 'tool' role. Tool results are embedded in assistant messages
 * or handled through the tool_use/tool_result mechanism within content blocks.
 * 
 * IMPORTANT: Tool results can be in the same message (as parts) or in the next message.
 * We check both cases to avoid stripping tool calls that have results in the same message.
 */
export function stripOrphanToolUse(messages: BedrockMessage[]): BedrockMessage[] {
  if (!messages || messages.length === 0) return messages;

  const out: BedrockMessage[] = [];

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (msg.role !== 'assistant' || typeof msg.content !== 'object' || !Array.isArray(msg.content)) {
      out.push(msg);
      continue;
    }

    const blocks = msg.content as BedrockContentBlock[];
    const hasToolUse = blocks.some((b) => typeof b === 'object' && b !== null && isToolUseBlock(b as Record<string, unknown>));

    if (!hasToolUse) {
      out.push(msg);
      continue;
    }

    // Check if current message has tool results (tool_result blocks within same message)
    const currentHasToolResult = blocks.some((b) => typeof b === 'object' && b !== null && 
      (b.type === 'tool-result' || b.type === 'tool_result' || 'result' in b || 'output' in b));

    if (currentHasToolResult) {
      // Current message has tool results: keep the tool_use blocks.
      out.push(msg);
      logger.debug('keeping tool_use (current message has tool results)', { op: 'stripOrphanToolUse', messageIndex: i });
      continue;
    }

    // Check if next message has tool results (tool_result blocks)
    const next = messages[i + 1];
    const nextHasToolResult = next && Array.isArray(next.content) && 
      next.content.some((b) => typeof b === 'object' && b !== null && 
        (b.type === 'tool-result' || b.type === 'tool_result' || 'result' in b || 'output' in b));

    if (nextHasToolResult) {
      // Next message has tool results: keep the tool_use blocks.
      out.push(msg);
      logger.debug('keeping tool_use (next message has tool results)', { op: 'stripOrphanToolUse', messageIndex: i });
      continue;
    }

    // No tool result following: strip tool_use blocks (orphan case).
    const kept = blocks.filter((b): b is BedrockContentBlock => {
      if (!b || typeof b !== 'object') return true;
      return !isToolUseBlock(b as Record<string, unknown>);
    });
    const content = kept.length > 0 ? kept : [PLACEHOLDER_BLOCK];
    if (kept.length < blocks.length) {
      logger.warn('stripped orphan tool_use from assistant message (no tool_result immediately after)', {
        op: 'stripOrphanToolUse',
        messageIndex: i,
        strippedCount: blocks.length - kept.length,
      });
    }
    out.push({ role: msg.role, content });
  }

  return out;
}

/**
 * Sanitize messages so Bedrock Converse API accepts them:
 * - Every message has at least one content block (no empty content).
 * - Every block that has a `text` property has non-blank text.
 * - Every assistant message has at least one part that survives the SDK's filter (so content never becomes []).
 * - Messages with tool parts but no text get a minimal text part to preserve tool information.
 */
export function sanitizeToBedrockMessages(
  messages: readonly ModelMessageInput[] | readonly { role: string; content: unknown }[]
): BedrockMessage[] {
  return messages.map((msg): BedrockMessage => {
    const role = msg.role as BedrockMessageRole;
    const content = msg.content;

    if (Array.isArray(content)) {
      if (content.length === 0) {
        logger.warn('empty content array, adding placeholder', { op: 'sanitizeToBedrockMessages' });
        return { role, content: [PLACEHOLDER_BLOCK] };
      }
      const blocks: BedrockContentBlock[] = content.map((block: unknown) => {
        if (!block || typeof block !== 'object') {
          return PLACEHOLDER_BLOCK;
        }
        return ensureBlockTextSafe(block as Record<string, unknown>);
      });
      
      // AI SDK strips tool-approval-request and empty text from assistant messages; ensure at least one part remains
      if (role === 'assistant') {
        const surviving = blocks.filter((b) => survivesSdkAssistantFilter(b as Record<string, unknown>));
        if (surviving.length === 0) {
          logger.warn('no surviving blocks in assistant message, adding placeholder', { op: 'sanitizeToBedrockMessages' });
          return { role, content: [PLACEHOLDER_BLOCK, ...blocks] };
        }
      }
      return { role, content: blocks };
    }

    if (typeof content === 'string') {
      const trimmed = content.trim();
      if (trimmed === '') {
        logger.warn('empty string content, using placeholder', { op: 'sanitizeToBedrockMessages' });
        return { role, content: '.' };
      }
      const safeContent = trimmed.length > MAX_CONTENT_BLOCK_CHARS
        ? truncateForBedrock(trimmed)
        : trimmed;
      if (safeContent !== trimmed) {
        logger.warn('truncated oversized string content', {
          op: 'sanitizeToBedrockMessages',
          originalLength: trimmed.length,
          maxChars: MAX_CONTENT_BLOCK_CHARS,
        });
      }
      return { role, content: safeContent };
    }

    // Unknown content shape: replace with placeholder so we never send empty
    logger.warn('unknown content shape, using placeholder', { op: 'sanitizeToBedrockMessages' });
    return { role, content: [PLACEHOLDER_BLOCK] };
  });
}
